import './Sidebar.css';
import React from 'react';
import classnames from 'classnames';
import Logo from './../../assets/ks_logo_vector.svg';
import { SidebarGroups } from './SidebarGroups/SidebarGroups';
import { SidebarLinks } from './SidebarLinks';
import { Icon } from '../Icon/Icon';
import { history } from '../../model/history';
import { writeDB } from '../../model/store';
import { useData } from '../../model/data';
import {
  dashboardPath,
  eventsPath,
  companiesPath,
  styleguidePath,
  venuesPath,
  eventTurnoversPath,
  toursPath,
  usersPath,
  productsPath,
  ordersPath,
  picklistsPath,
  fulfillmentPath,
  queuePath,
  shopsPath,
  newsPath,
  contractsPath,
  bookKeepingExportPath,
  categoriesPath,
  settingsPath,
  backPaymentsPath,
  merchStatisticsPath,
  chartsPath,
  voucherCampaignsPath,
} from '../../routes';
import {
  ticketingActivated,
  isAdmin,
  merchActivated,
  ordersActivated,
  useSelectedMembership,
} from '../../model/membership';
import { t, I18n } from '../../locales/translate';
import { Button } from '../Button/Button';
import { formatDateTime } from '../../util/util';
import { Tooltip } from '../Tooltip/Tooltip';
import { buildInfo } from '../../model/version';
import { compact } from 'lodash';

export interface Link {
  icon?: React.ReactNode;
  text: string;
  secondary?: boolean;
  path?: string;
  links?: Link[];
}

export function Sidebar(): JSX.Element {
  const [open] = useData((db) => [db.get('sidebarOpen')]);
  const membership = useSelectedMembership();

  function canIndex(model: string): boolean {
    return !!membership?.indexPermissions.includes(model);
  }

  const ticketAdminOrOwner =
    isAdmin(membership) || membership?.permissions?.includes('ticketing_admin') || membership?.role === 'owner';

  const links: Array<Link> = compact([
    {
      icon: <Icon name="house" />,
      text: t.page.dashboard.title,
      path: dashboardPath(),
    },
    ticketingActivated(membership) && {
      icon: <Icon name="ticket" />,
      text: t.model.ticket.ticketing,
      links: compact([
        canIndex('Event') && {
          text: t.model.event._plural,
          path: eventsPath(),
        },
        canIndex('Tour') && {
          text: t.model.tour._plural,
          path: toursPath(),
        },
        canIndex('Venue') && {
          text: t.model.venue._plural,
          path: venuesPath(),
        },
        ticketAdminOrOwner && {
          text: t.page.turnover.menuTitle,
          path: eventTurnoversPath(),
        },
      ]),
    },
    merchActivated(membership) && {
      icon: <Icon name="shirt" />,
      text: t.model.product.merchandise,
      links: compact([
        canIndex('MerchProduct') && {
          text: t.model.product._plural,
          path: productsPath(),
        },
        canIndex('Category') && {
          text: t.model.category._plural,
          path: categoriesPath(),
        },
        {
          text: t.model.merchStatistics._name,
          path: merchStatisticsPath(),
        },
      ]),
    },
    ordersActivated(membership) && {
      icon: <Icon name="money" />,
      text: t.model.order._plural,
      links: compact([
        canIndex('Order') && {
          text: t.model.order._plural,
          path: ordersPath(),
        },
        canIndex('Picklist') && {
          text: t.model.picklist._plural,
          path: picklistsPath(),
        },
        {
          text: t.page.sidebar.fulfillment,
          path: fulfillmentPath(),
        },
        {
          text: t.page.sidebar.backPayments,
          path: backPaymentsPath(),
        },
      ]),
    },
    isAdmin(membership) && {
      icon: <Icon name="settings" />,
      text: t.page.sidebar.contentAndCampaigns,
      links: compact([
        canIndex('News') && {
          text: t.model.news._plural,
          path: newsPath(),
        },
        canIndex('VoucherCampaign') && {
          text: t.page.sidebar.voucherCampaigns,
          path: voucherCampaignsPath(),
        },
      ]),
    },
    isAdmin(membership) && {
      text: t.model.user.admin,
      links: compact([
        canIndex('Contract') && { text: t.model.contract._plural, path: contractsPath() },
        {
          text: t.model.shop._plural,
          path: shopsPath(),
        },
        { text: t.page.sidebar.settings, path: settingsPath() },
        { text: t.page.queue.title, path: queuePath() },
        { text: t.page.styleguide.title, path: styleguidePath() },
      ]),
      icon: <Icon name="user-plus" />,
    },
    canIndex('Company') && {
      text: t.model.company._plural,
      secondary: true,
      path: companiesPath(),
    },
    canIndex('User') && {
      text: 'Users',
      secondary: true,
      path: usersPath(),
    },
    isAdmin(membership) && {
      text: t.model.bookkeeping._name,
      secondary: true,
      path: bookKeepingExportPath(),
    },
    isAdmin(membership) && {
      text: t.model.charts._name,
      secondary: true,
      path: chartsPath(),
    },
  ]).filter((link) => !link.links || link.links.length > 0);
  return (
    <aside className={classnames('sidebar', { 'sidebar--open': open })}>
      <header className="sidebar__header">
        <div className="sidebar__toggle" onClick={() => writeDB.set('sidebarOpen', false)}>
          <Icon name="menu" />
        </div>
        <div className="sidebar__logo">
          <img
            src={Logo}
            alt="kasserstoff.com"
            onClick={() => {
              history.push(dashboardPath());
            }}
          />
        </div>
      </header>
      <div className="sidebar__body">
        <section className="sidebar__section">
          <SidebarGroups />
        </section>
        <section className="sidebar__section sidebar__links">
          <SidebarLinks links={links} onSelectLink={() => writeDB.set('sidebarOpen', false)} />
        </section>
      </div>
      <div className="sidebar__footer">
        <div className="sidebar__lang-select">
          <Button type="link" title={t.general.germanShort} onClick={() => (I18n.locale = 'de')} />
          <span> / </span>
          <Button type="link" title={t.general.englishShort} onClick={() => (I18n.locale = 'en')} />
        </div>
        <div className="sidebar__build">
          <Tooltip
            reference={
              <small>
                {t.page.sidebar.build}: {buildInfo.buildShort}
              </small>
            }
          >
            <small>
              {t.page.sidebar.lastUpdatedAt}: {formatDateTime(buildInfo.lastUpdatedAt)}
            </small>
          </Tooltip>
        </div>
      </div>
    </aside>
  );
}
